import { Link } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import SEOPageLayout from "@/components/SEOPageLayout";
import { COOK_ISLANDS_KEYWORDS, joinKw } from "@/config/keywords";

const HERO_IMG =
  "https://images.unsplash.com/photo-1585071258252-369a36d89e30?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

export default function CookIslandsUniforms() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Cook Islands Uniforms",
    serviceType: "Custom Cook Islands polo uniform design and manufacturing",
    provider: {
      "@type": "LocalBusiness",
      name: "Tribal Designz",
      address: { "@type": "PostalAddress", addressLocality: "Auckland", addressCountry: "NZ" },
      telephone: "+64 22 101 2443",
    },
  };

  const imageObjects = [{
    url: HERO_IMG,
    alt: "Cook Islands Church Uniforms — custom Tivaevae pattern Pacific Polo Shirts CICC Auckland NZ",
    name: "Cook Islands Uniforms — Tivaevae Polo",
    representative: true,
  }];

  return (
    <>
      <SEOHead
        title="Cook Islands Uniforms NZ | Custom Polo Shirts"
        description="Cook Islands Church Uniforms NZ — custom polo shirts for CICC, CIPC, community groups, reunions & Maeva Nui. Tivaevae and tapa-inspired designs from Auckland."
        path="/cook-islands-uniforms"
        image={HERO_IMG}
        imageAlt="Cook Islands Uniforms — Tivaevae Pattern Polo Shirts NZ"
        keywords={joinKw(COOK_ISLANDS_KEYWORDS)}
        jsonLd={jsonLd}
        imageObjects={imageObjects}
      />
      <SEOPageLayout
        heroEyebrow="CICC · CIPC · Catholic · AOG"
        heroH1="Cook Islands Uniforms — Custom Polo Shirts NZ"
        heroSub="Authentic Cook Islands polo uniforms for churches, community groups, reunions and cultural festivals. Tivaevae and tapa-inspired designs from our Auckland studio."
        heroImage={HERO_IMG}
        heroImageAlt="Custom Cook Islands polo uniform with tivaevae pattern Auckland NZ"
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Cook Islands Uniforms" }]}
      >
        <h2>Designed for Cook Islands communities across Aotearoa</h2>
        <p>
          The Cook Islands community in New Zealand carries deep cultural
          pride — and your uniforms should carry it too. Whether you're
          organising your church's annual Nuku, planning a Maeva Nui dance
          group, coordinating a family reunion that spans Rarotonga to
          Aitutaki to Auckland, or fitting out your CICC ushers and choir,
          custom <strong>Cook Islands uniforms</strong> from Tribal Designz
          help your community show up unified and proud.
        </p>
        <p>
          We design polo uniforms for Cook Islands churches (CICC, CIPC,
          Catholic, AOG), community groups, ei pa enua associations, dance
          groups, sports teams and family reunions across Auckland and NZ.
        </p>

        <h2>Cultural design elements we work with</h2>
        <ul>
          <li>
            <strong>Tivaevae patterns</strong> — appliqué quilt-inspired
            florals and geometric motifs translated into polo print
          </li>
          <li>
            <strong>Tapa (kiriau) designs</strong> — traditional Cook
            Islands tapa patterns as panel work
          </li>
          <li>
            <strong>Island (enua) crests</strong> — Rarotonga, Aitutaki,
            Atiu, Mangaia, Mauke, Mitiaro, Manihiki, Pukapuka, Penrhyn
          </li>
          <li>
            <strong>Maeva Nui</strong> and other major cultural event
            branding
          </li>
          <li>Ei (lei) and hibiscus illustrations</li>
          <li>Church name and minister embroidery in Cook Islands Māori + English</li>
          <li>Family name (kopu tangata) for reunions</li>
        </ul>

        <h2>Who we outfit</h2>
        <ul>
          <li><strong>CICC &amp; CIPC churches</strong> — ushers, choirs, women's &amp; men's fellowship, youth ministry</li>
          <li><strong>Dance groups (pātē, drum &amp; dance)</strong> — performance team polos &amp; supporter merch</li>
          <li><strong>Ei pa enua associations</strong> — island-specific community group uniforms</li>
          <li><strong>Family / kopu tangata reunions</strong> — multi-generation polo sets</li>
          <li><strong>Cook Islands sports teams</strong> — rugby, netball, touch</li>
          <li><strong>Cultural festivals</strong> — Maeva Nui, Pasifika Festival, community day events</li>
        </ul>

        <h2>Polo fabric &amp; quality</h2>
        <p>
          Our polo blanks are a mid-weight cotton/poly blend — soft enough
          to wear all day, structured enough to hold their shape under
          embroidery and full-colour print, and breathable enough for warm
          weather (whether you're at a Cook Islands community event in
          January or a humid Auckland summer). Sizes XS–7XL.
        </p>

        <h2>Cultural integrity matters to us</h2>
        <p>
          We're a Pacific-led studio. We don't pluck patterns off the
          internet and slap them on a polo. If you want tivaevae motifs,
          we'll work with you to understand which specific motifs your
          family or church already uses, what they mean, and how to
          translate them into print without losing their integrity. The
          same goes for tapa work and island-specific patterns.
        </p>

        <h2>Pricing &amp; lead times</h2>
        <p>
          Bulk polo pricing starts at <strong>$59 per polo</strong> for
          orders of 10–24 and drops to <strong>$49 per polo</strong> at 50+
          units. Standard production turnaround is 2–3 weeks. Plan major
          Cook Islands event orders 4–6 weeks ahead.
        </p>

        <h2>Related collections</h2>
        <p>
          <Link to="/samoan-church-uniforms">Samoan church uniforms</Link> ·{" "}
          <Link to="/tongan-church-uniforms">Tongan church uniforms</Link> ·{" "}
          <Link to="/reunion-polo-shirts">Reunion polo shirts</Link>
        </p>
      </SEOPageLayout>
    </>
  );
}
